import Chip from '@mui/material/Chip';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import { ConsultationEdge } from '@/__generated__/graphql';
import Link from '@/components/Link';
import Picture from '@/components/Picture';
import { parseDuration } from '@/consultation';
import { RootState, useAppSelector } from '@/store';
import { getInitials } from '@/utils/getInitials';
import { stringToColor } from '@/utils/stringToColor';

import { DashboardCard } from './DashboardCard';

export function getDayLabel(date: moment.Moment): string {
  const today = moment().startOf('day');
  const target = date.clone().startOf('day');
  const diff = target.diff(today, 'days');

  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return date.format('dddd, MMM D');
}

export function isJoinable(startsAt: string): boolean {
  const now = moment();
  const start = moment(startsAt);
  return now.isAfter(start.clone().subtract(15, 'minutes'));
}

// A call is no longer upcoming once its scheduled end has passed, even while the
// finalize routine has yet to close it out. An in-progress call that overruns its
// slot stays listed so participants can rejoin. Written responses keep their own
// deadline semantics and are never treated as ended here. ENG-5305.
export function hasCallEnded(
  node: Pick<
    ConsultationEdge['node'],
    'state' | 'starts_at' | 'expected_duration' | 'engagement_type'
  >,
  now: moment.Moment = moment()
): boolean {
  if (node.engagement_type === 'written_response') return false;
  if (node.state === 'in_progress') return false;
  if (!node.starts_at || !node.expected_duration) return false;
  const endsAt = moment(node.starts_at).add(parseDuration(node.expected_duration).milliseconds());
  return !now.isBefore(endsAt);
}

function UserAvatar({
  user,
  name,
}: {
  user:
    | { first_name?: string | null; last_name?: string | null; picture_url?: string | null }
    | null
    | undefined;
  name: string;
}) {
  const hasPicture = !!user?.picture_url && user.picture_url.trim() !== '';
  if (hasPicture) return <Picture user={user} size={32} link={false} />;

  const initials = getInitials(user?.first_name, user?.last_name);
  const bgColor = stringToColor(name, 700);
  return (
    <div
      className="flex h-[3.2rem] w-[3.2rem] shrink-0 items-center justify-center rounded-full text-12 font-semibold text-white"
      style={{ backgroundColor: bgColor }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

interface UpcomingCallsCardProps {
  fullWidth?: boolean;
  /** Card heading; the team My-activity layout overrides to "My Upcoming Calls". */
  title?: string;
  collapseButton?: React.ReactNode;
  collapsed?: boolean;
}

export function UpcomingCallsCard({
  fullWidth,
  title = 'Upcoming Calls',
  collapseButton,
  collapsed,
}: UpcomingCallsCardProps): React.ReactNode {
  const viewer = useAppSelector((state: RootState) => state.viewer);
  const userContext = useAppSelector((state: RootState) => state.ui.userContext);
  // Only scheduled consultations are upcoming. A negotiating consultation can carry
  // the start time of a call it replaced (an expert rescheduling a confirmed call),
  // so the awaiting collection is not read here. ENG-5305.
  const confirmed = useAppSelector((state: RootState) => state.consultations.dashboardConfirmed);

  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 5_000);
    return () => clearInterval(interval);
  }, []);

  const loading = confirmed.loading;

  const edges = (confirmed.edges || []) as ConsultationEdge[];

  const withDates = edges.filter((e) => !!e.node.starts_at && !hasCallEnded(e.node));

  if (withDates.length === 0) {
    return (
      <DashboardCard
        title={title}
        loading={loading}
        fullWidth={fullWidth}
        collapseButton={collapseButton}
        collapsed={collapsed}
      >
        <p className="text-12 text-on-surface-muted">No upcoming calls scheduled.</p>
      </DashboardCard>
    );
  }
  const sorted = [...withDates]
    .sort((a, b) => moment(a.node.starts_at).valueOf() - moment(b.node.starts_at).valueOf())
    .slice(0, 5);

  const grouped = new Map<string, ConsultationEdge[]>();
  for (const edge of sorted) {
    const label = getDayLabel(moment(edge.node.starts_at));
    const group = grouped.get(label) || [];
    group.push(edge);
    grouped.set(label, group);
  }

  function getDisplayUser(c: ConsultationEdge['node']) {
    const isExpert = viewer.id === c.expert?.id;
    const isAdmin = userContext === 'admin';
    if (isExpert || isAdmin) {
      return {
        user: c.requester,
        name: c.requester?.name || c.requester_name || 'Confidential client',
      };
    }
    return {
      user: c.expert,
      name: c.expert?.name || c.expert_name || 'Expert',
    };
  }

  return (
    <DashboardCard
      title={title}
      badge={withDates.length}
      linkText="View all consultations"
      linkTo="/consultations"
      loading={loading}
      fullWidth={fullWidth}
      collapseButton={collapseButton}
      collapsed={collapsed}
    >
      <div className="flex flex-col gap-12">
        {[...grouped.entries()].map(([label, items]) => (
          <div key={label} role="group" aria-label={label}>
            <div className="mb-6 text-[1.1rem] font-semibold uppercase tracking-widest text-on-surface-muted">
              {label}
            </div>
            <div className="flex flex-col gap-4">
              {items.map((edge) => {
                const c = edge.node;
                const { user, name } = getDisplayUser(c);
                const expertHeadline = (c.expert as { headline?: string | null })?.headline;
                const projectName = (c.expert_request as { project?: { name?: string } })?.project
                  ?.name;
                const requestName = c.expert_request?.name;
                const isWrittenResponse = c.engagement_type === 'written_response';
                const joinable =
                  !isWrittenResponse && c.state === 'confirmed' && isJoinable(c.starts_at ?? '');

                const title =
                  projectName ||
                  requestName ||
                  (isWrittenResponse ? 'Written response' : 'Consultation');
                const engagementLabel = isWrittenResponse
                  ? 'written response'
                  : expertHeadline || (c.engagement_type ?? '').replace(/_/g, ' ');
                const showName = name && name !== title;

                return (
                  <div
                    key={c.id}
                    className="flex items-center gap-10 rounded-sm px-10 py-8 transition-colors hover:bg-surface-elevated"
                  >
                    <Link
                      to={c.html_url}
                      className="flex min-w-0 flex-1 items-center gap-10 no-underline"
                    >
                      <UserAvatar user={user} name={name} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-14 font-medium text-on-surface">{title}</div>
                        <div className="truncate text-12 text-on-surface-muted">
                          <span>{moment(c.starts_at).format('h:mm A')}</span>
                          {engagementLabel && <span>{` \u00B7 ${engagementLabel}`}</span>}
                          {showName && <span>{` \u00B7 ${name}`}</span>}
                        </div>
                      </div>
                    </Link>
                    {isWrittenResponse ? (
                      <Link
                        to={c.html_url}
                        className="inline-flex shrink-0 items-center rounded-md border border-surface-border bg-surface-elevated px-10 py-4 text-12 font-semibold text-on-surface no-underline shadow-5 transition-shadow hover:bg-surface-secondary hover:shadow-1"
                      >
                        Open
                      </Link>
                    ) : joinable ? (
                      <Link
                        to={`${c.html_url}?join_call=true`}
                        className="inline-flex shrink-0 items-center rounded-md bg-brand-primary px-10 py-4 text-12 font-semibold text-surface-card no-underline shadow-5 transition-shadow hover:shadow-1"
                      >
                        Join Call
                      </Link>
                    ) : (
                      <Chip
                        label={moment(c.starts_at).fromNow()}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '11px', height: 22 }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </DashboardCard>
  );
}
